import * as React from 'react';

import { useTranslation } from '@/hooks/useTranslation';

import { FileUploadDetailsItem } from './FileUploadDetailsItem';
import { type UploadItem } from './types';

interface FileUploadDetailsProps {
    uploads: UploadItem[];
    isMultiple?: boolean;
    onRemove?: (item: UploadItem) => void;
    onRetry?: (item: UploadItem) => void;
}

function getTitleKey(
    uploads: UploadItem[],
    isMultiple: boolean,
): { key: string; values?: Record<string, number> } {
    const uploading = uploads.filter((u) => u.status === 'uploading');

    if (isMultiple) {
        if (uploading.length > 0) {
            return { key: 'UPLOADER.UPLOADING_MANY', values: { count: uploading.length } };
        }
        return { key: 'UPLOADER.UPLOADED_FILES' };
    }
    if (uploading.length > 0) {
        return { key: 'UPLOADER.UPLOADING_ONE' };
    }
    return { key: 'UPLOADER.UPLOADED_FILE' };
}

const FileUploadDetails = ({
    uploads,
    isMultiple = false,
    onRemove,
    onRetry,
}: FileUploadDetailsProps) => {
    const { t } = useTranslation();

    const { key, values } = getTitleKey(uploads, isMultiple);
    const titleNode = t(key, values);

    return (
        <div className="tw-flex tw-flex-col tw-gap-3 tw-w-full">
            <div className="tw-text-t-base lg:tw-text-t-lg tw-font-bold tw-text-h-primary tw-w-full">
                {titleNode}
            </div>
            {/*
             * Reserve Figma's 96 px placeholder (file row + gap + error line)
             * for both single- and multi-upload variants so the enclosing
             * dialog does not jump between empty / success / uploading /
             * error states. See Figma node 4408:2736 ("File Upload Details
             * Container").
             */}
            <div
                className="tw-flex tw-flex-col tw-w-full tw-gap-2 tw-h-24"
                data-testid="uploader-file-list"
            >
                {uploads.map((item, index) => (
                    <React.Fragment key={item.id}>
                        {item.group && item.group.id !== uploads[index - 1]?.group?.id && (
                            <div
                                className="tw-text-t-sm tw-font-bold tw-text-t-secondary tw-mt-2"
                                data-testid={`uploader-file-group-${item.group.id}`}
                            >
                                {item.group.label}
                            </div>
                        )}
                        <FileUploadDetailsItem
                            item={item}
                            onRemove={onRemove}
                            onRetry={onRetry}
                        />
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

FileUploadDetails.displayName = 'FileUploadDetails';

export { FileUploadDetails };
